import { cva, type VariantProps } from 'class-variance-authority';
import type { HTMLAttributes, Ref } from 'react';
import { cn } from '@/lib/utils';

const cardVariants = cva('flex flex-col gap-6 rounded-xl border bg-card text-card-foreground shadow', {
  variants: {
    variant: {
      default: 'border-border/70 bg-card',
      solid: 'border-border/70 bg-card/90',
      glass: 'border-border/60 bg-card/70 backdrop-blur-sm supports-[backdrop-filter]:bg-card/60',
      settings: 'border-border/70 bg-card/90 backdrop-blur-md supports-[backdrop-filter]:bg-card/80',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

type CardProperties = HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof cardVariants> & { ref?: Ref<HTMLDivElement> };

const Card = ({ className, variant, ref, ...properties }: CardProperties) => (
  <div ref={ref} className={cn(cardVariants({ variant }), className)} {...properties} />
);

type DivProperties = HTMLAttributes<HTMLDivElement> & { ref?: Ref<HTMLDivElement> };

const CardHeader = ({ className, ref, ...properties }: DivProperties) => (
  <div ref={ref} className={cn('flex flex-col space-y-1.5 px-6 pt-6', className)} {...properties} />
);

const CardTitle = ({ className, ref, ...properties }: DivProperties) => (
  <div ref={ref} className={cn('font-semibold leading-none tracking-tight', className)} {...properties} />
);

const CardDescription = ({ className, ref, ...properties }: DivProperties) => (
  <div ref={ref} className={cn('text-sm text-muted-foreground', className)} {...properties} />
);

const CardContent = ({ className, ref, ...properties }: DivProperties) => (
  <div ref={ref} className={cn('px-6 pb-6', className)} {...properties} />
);

const CardFooter = ({ className, ref, ...properties }: DivProperties) => (
  <div ref={ref} className={cn('flex items-center px-6 pb-6', className)} {...properties} />
);

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
